import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import { logError } from '../../utils/errors';
import { isMobileApp } from '../../utils/getHost';

/**
 * Catches any error in a child component and shows a fallback UI.
 */
class ErrorBoundary extends React.Component {
    constructor() {
        super();

        this.state = {
            hasError: false,
        };

        this.reset = this.reset.bind(this);
    }

    static getDerivedStateFromError(error) {
        // Handle chunk load errors as a special case as this almost certainly means we did an update and user just needs to refresh
        return { hasError: true, newRelease: /^Loading chunk/.test(error.message) };
    }

    componentDidCatch(error, errorInfo) {
        logError(error, { identifier: 'ErrorBoundary', errorBoundaryMessage: this.props.message });
    }

    reset() {
        this.setState({ hasError: false });
    }

    render() {
        const { children, errorComponent, message } = this.props;

        if (this.state.hasError) {

            if (isMobileApp && this.state.newRelease) {
                // Automatically reload mobile app when a chunk cannot be fetched as we've done an update
                return (
                    <MobileAppReload />
                );
            }

            const ErrorComponent = errorComponent;
            return (
                <>
                    <LocationObserver reset={this.reset} />
                    <ErrorComponent message={message} newRelease={this.state.newRelease} />
                </>
            );
        }

        return children;
    }
}

// Reset `hasError` whenever location changes
// This stops us being stuck in error state
const LocationObserver = ({ reset }) => {
    const location = useLocation();
    const firstUpdate = useRef(true);

    useEffect(() => {
        // useEffect runs at start and then again when any value in dependency array changes
        // Only want to call reset() when location changes, NOT at start
        // firstUpdate records whether this is first useEffect call or not
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }

        reset();
    }, [location, reset]);

    return null;
};

// Component to handle reloading on mobile app which we do when a chunk cannot be fetched as we have since done an update
const MobileAppReload = () => {

    useEffect(() => {
        // Do not reload if we have already just done so (something is obviously broken in this case)
        // This ensures we don't get stuck in a constant refresh loop
        const lastReloadTime = sessionStorage.lastMobileAppReload ? parseInt(sessionStorage.lastMobileAppReload) : 0;
        if (Date.now() - lastReloadTime > 30000) {
            sessionStorage.lastMobileAppReload = Date.now();
            window.location.reload();
        } else {
            console.warn(`Chunk failed to fetch but last reload was only ${Date.now() - lastReloadTime}ms ago so not reloading again`);
        }
    }, []);

    return null;
}

ErrorBoundary.propTypes = {
    /** Error message to display. */
    message: PropTypes.string.isRequired,
    /** Component to render instead of children when an error occurs. */
    errorComponent: PropTypes.elementType.isRequired,
};

export default ErrorBoundary;
